import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import supabase from '../supabase/supabase';

/* ─────────────────────────────────────────
   Helpers
───────────────────────────────────────── */
function getLocalTime() {
  return new Date().toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true
  });
}

function getDateString() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric'
  }).toUpperCase();
}

function pad(n) {
  return String(n).padStart(2, '0');
}

/* ─────────────────────────────────────────
   Orientation hook — re-fires on rotation
───────────────────────────────────────── */
function useIsPortrait() {
  const [isPortrait, setIsPortrait] = useState(
    () => window.matchMedia('(orientation: portrait)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(orientation: portrait)');
    const handler = (e) => setIsPortrait(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isPortrait;
}

/* ─────────────────────────────────────────
   Fractional color logic
───────────────────────────────────────── */
function getFractionalState(timeRemaining, totalDuration) {
  if (!totalDuration) return { color: '#ffffff', pulse: false, key: 'default' };
  const half    = Math.round(totalDuration / 2);
  const quarter = Math.round(totalDuration / 4);
  if (timeRemaining <= 300) {
    return { color: '#D4AF37', pulse: false, key: 'warning' };
  }
  if (timeRemaining <= quarter && timeRemaining > quarter - 4) {
    return { color: '#D4AF37', pulse: true, key: 'pulse-quarter' };
  }
  if (timeRemaining <= half && timeRemaining > half - 4) {
    return { color: '#D4AF37', pulse: true, key: 'pulse-half' };
  }
  return { color: '#ffffff', pulse: false, key: 'default' };
}

function getCountUpFractionalState(timeElapsed, totalDuration) {
  return getFractionalState(
    Math.max(0, totalDuration - timeElapsed),
    totalDuration
  );
}

/* ─────────────────────────────────────────
   Display Kiosk — Talent View (Reader)
───────────────────────────────────────── */
export default function DisplayKiosk() {
  const params = useParams();
  const roomId = params.roomId;
  const isPortrait = useIsPortrait();

  useEffect(() => { document.title = 'studiotimer / display'; }, []);

  const [sessionData, setSessionData]     = useState(null);
  const [localTime, setLocalTime]         = useState(getLocalTime());
  const [dateString, setDateString]       = useState(getDateString());
  const [mode, setMode]                   = useState('idle');
  const [targetMode, setTargetMode]       = useState('idle');
  const [visible, setVisible]             = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [preRollSec, setPreRollSec]       = useState(0);
  const [fracState, setFracState]         = useState({ color: '#ffffff', pulse: false, key: 'default' });
  const [liveCue, setLiveCue]             = useState('');
  const [showCue, setShowCue]             = useState(false);
  const [showOrientHint, setShowOrientHint] = useState(false);
  const cueTimeoutRef                     = useRef(null);
  const hintTimeoutRef                    = useRef(null);
  const serverTimeOffset                  = useRef(0);
  const wakeLockRef                       = useRef(null);

  /* ── Orientation hint: show for 6s when portrait is detected ── */
  useEffect(() => {
    if (isPortrait) {
      setShowOrientHint(true);
      if (hintTimeoutRef.current) clearTimeout(hintTimeoutRef.current);
      hintTimeoutRef.current = setTimeout(() => setShowOrientHint(false), 6000);
    } else {
      setShowOrientHint(false);
    }
    return () => { if (hintTimeoutRef.current) clearTimeout(hintTimeoutRef.current); };
  }, [isPortrait]);

  /* ── Wake Lock API ── */
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator)
          wakeLockRef.current = await navigator.wakeLock.request('screen');
      } catch (err) { console.warn('Wake Lock error:', err); }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    };
    requestWakeLock();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  /* ── Fullscreen Toggle (double-tap) ── */
  const handleDoubleClick = async () => {
    try {
      const isFS = document.fullscreenElement || document.webkitFullscreenElement;
      if (!isFS) {
        const el = document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
      }
    } catch (err) { console.warn('Fullscreen toggle failed:', err); }
  };

  /* ── DB Initialization & Realtime Subscription ── */
  useEffect(() => {
    if (!roomId) return;
    let channel;

    const fetchSession = async () => {
      const { data: session } = await supabase
        .from('studio_sessions').select('*').eq('room_id', roomId).single();
      if (session) setSessionData(session);
    };

    const initKiosk = async () => {
      try {
        const { data: serverTime, error: timeErr } = await supabase.rpc('get_server_time');
        if (!timeErr && serverTime)
          serverTimeOffset.current = new Date(serverTime).getTime() - Date.now();
      } catch (err) { console.warn('Clock sync failed:', err); }

      await fetchSession();

      channel = supabase.channel(`public:studio_sessions:${roomId}`)
        .on('postgres_changes', {
          event: '*', schema: 'public',
          table: 'studio_sessions', filter: `room_id=eq.${roomId}`
        }, (payload) => {
          if (payload.new && payload.new.room_id === roomId)
            setSessionData(payload.new);
        })
        .subscribe();
    };

    initKiosk();
    const handleOnline = () => fetchSession();
    window.addEventListener('online', handleOnline);
    return () => {
      if (channel) supabase.removeChannel(channel);
      window.removeEventListener('online', handleOnline);
    };
  }, [roomId]);

  /* ── Smooth Fader Engine ── */
  useEffect(() => {
    if (targetMode !== mode) {
      setVisible(false);
      const t = setTimeout(() => setMode(targetMode), 400);
      return () => clearTimeout(t);
    } else if (!visible) {
      const t = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(t);
    }
  }, [targetMode, mode, visible]);

  /* ── Absolute Source of Truth Computation Engine ── */
  const currentModeRef = useRef('idle');

  useEffect(() => {
    if (!sessionData) return;
    let animationFrameId;

    const changeMode = (newMode) => {
      if (currentModeRef.current !== newMode) {
        currentModeRef.current = newMode;
        setTargetMode(newMode);
      }
    };

    if (sessionData.status === 'idle') changeMode('idle');

    const tick = () => {
      if (sessionData.status === 'paused') {
        const pausedRem = sessionData.paused_time_remaining || 0;
        setTimeRemaining(prev => {
          if (prev !== pausedRem)
            setFracState(sessionData.timer_mode === 'up'
              ? getCountUpFractionalState(pausedRem, sessionData.total_duration_seconds)
              : getFractionalState(pausedRem, sessionData.total_duration_seconds));
          return pausedRem;
        });
        changeMode('paused');
        animationFrameId = requestAnimationFrame(tick);
        return;
      }

      const now = Date.now() + serverTimeOffset.current;

      if (sessionData.status === 'active' && sessionData.timer_target) {
        const targetMs        = new Date(sessionData.timer_target).getTime();
        const totalDurationMs = sessionData.total_duration_seconds * 1000;
        const startTimeMs     = targetMs - totalDurationMs;
        const timerStartMs    = sessionData.timer_mode === 'up' ? targetMs : startTimeMs;

        if (sessionData.pre_roll_duration_seconds > 0 && now < timerStartMs) {
          const preRollRem     = Math.max(0, Math.ceil((timerStartMs - now) / 1000));
          const clampedPreRoll = Math.min(preRollRem, sessionData.pre_roll_duration_seconds);
          setPreRollSec(clampedPreRoll);
          changeMode('preroll');
        } else if (sessionData.timer_mode === 'up') {
          const elapsed = Math.max(0, Math.ceil((now - targetMs) / 1000));
          setTimeRemaining(prev => {
            if (prev !== elapsed)
              setFracState(getCountUpFractionalState(elapsed, sessionData.total_duration_seconds));
            return elapsed;
          });
          changeMode('active');
        } else if (now < targetMs) {
          const rem        = Math.max(0, Math.ceil((targetMs - now) / 1000));
          const clampedRem = Math.min(rem, sessionData.total_duration_seconds || rem);
          setTimeRemaining(prev => {
            if (prev !== clampedRem)
              setFracState(getFractionalState(clampedRem, sessionData.total_duration_seconds));
            return clampedRem;
          });
          changeMode('active');
        } else {
          setTimeRemaining(0);
          changeMode('complete');
        }
      } else if (sessionData.status === 'complete') {
        setTimeRemaining(0);
        changeMode('complete');
      } else if (sessionData.status === 'idle') {
        changeMode('idle');
      }

      animationFrameId = requestAnimationFrame(tick);
    };

    animationFrameId = requestAnimationFrame(tick);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (sessionData.status === 'paused') {
          const pausedRem = sessionData.paused_time_remaining || 0;
          setTimeRemaining(prev => {
            if (prev !== pausedRem)
              setFracState(sessionData.timer_mode === 'up'
                ? getCountUpFractionalState(pausedRem, sessionData.total_duration_seconds)
                : getFractionalState(pausedRem, sessionData.total_duration_seconds));
            return pausedRem;
          });
          return;
        }
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelAnimationFrame(animationFrameId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [sessionData]);

  /* ── Live Cues Observer ── */
  const isCuesAllowed = mode === 'active' || mode === 'paused';

  useEffect(() => {
    if (sessionData?.live_cue && isCuesAllowed) {
      setLiveCue(sessionData.live_cue);
      setShowCue(true);
      if (cueTimeoutRef.current) clearTimeout(cueTimeoutRef.current);
      cueTimeoutRef.current = setTimeout(() => setShowCue(false), 15000);
    } else {
      setShowCue(false);
    }
  }, [sessionData?.live_cue, isCuesAllowed]);

  /* ── Wall-clock tick ── */
  useEffect(() => {
    const id = setInterval(() => {
      setLocalTime(getLocalTime());
      setDateString(getDateString());
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const activeMinutes = Math.floor(timeRemaining / 60);
  const activeSeconds = timeRemaining % 60;
  const activeDisplay = `${pad(activeMinutes)}:${pad(activeSeconds)}`;

  return (
    <div
      className="relative w-screen h-screen overflow-hidden select-none"
      style={{ backgroundColor: '#000000' }}
      onDoubleClick={handleDoubleClick}
    >
      {/* ── Main content layer ── */}
      <div
        className="absolute inset-0 flex items-center justify-center cursor-default"
        style={{ transition: 'opacity 400ms ease-in-out', opacity: visible ? 1 : 0 }}
      >
        {mode === 'idle'     && <IdleClock time={localTime} date={dateString} isPortrait={isPortrait} />}
        {mode === 'preroll'  && <PreRollCountdown seconds={preRollSec} />}
        {mode === 'active'   && <ActiveCountdown display={activeDisplay} paused={false} fracState={fracState} />}
        {mode === 'paused'   && <ActiveCountdown display={activeDisplay} paused={true}  fracState={fracState} />}
        {mode === 'complete' && <CompleteState />}
      </div>

      {/* ── Live Cue — bottom-anchored, fluid ── */}
      <div
        className="absolute left-0 w-full flex justify-center pointer-events-none z-50"
        style={{
          bottom: isPortrait ? '8%' : '10%',
          opacity: showCue ? 1 : 0,
          transition: 'opacity 700ms ease-in-out'
        }}
      >
        <span
          className="uppercase text-center text-white px-6"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 'var(--kiosk-cue-size)',
            fontWeight: 600,
            letterSpacing: '0.25em'
          }}
        >
          {liveCue}
        </span>
      </div>

      {/* ── Orientation hint — portrait only, auto-fades ── */}
      <div
        className="orientation-hint absolute bottom-0 left-0 w-full items-center justify-center z-40 pointer-events-none"
        style={{
          padding: 'clamp(0.8rem, 2.5vmin, 1.5rem)',
          opacity: showOrientHint ? 0.55 : 0,
          transition: 'opacity 800ms ease-in-out'
        }}
      >
        <div
          className="flex items-center gap-2 rounded-full border border-[#2a2a2a] bg-[#0f0f0f]"
          style={{
            padding: 'clamp(0.4rem, 1.2vmin, 0.7rem) clamp(0.9rem, 2.8vmin, 1.4rem)'
          }}
        >
          {/* Rotate icon — pure SVG, no dependency */}
          <svg
            width="clamp(12px, 2.5vmin, 18px)"
            height="clamp(12px, 2.5vmin, 18px)"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#888888"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(0.65rem, 1.8vmin, 0.85rem)',
              color: '#888888',
              letterSpacing: '0.12em',
              fontWeight: 500,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap'
            }}
          >
            Best viewed in landscape
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   IdleClock

   LANDSCAPE: horizontal row — time + AM/PM side by side.
   PORTRAIT:  vertical column — time on top, AM/PM below.
   Both read from CSS custom props so the @media query
   in index.css handles all the size switching cleanly.
───────────────────────────────────────── */
function IdleClock({ time, date, isPortrait }) {
  const [timePart, meridiem] = time.split(' ');

  return (
    <div className="flex flex-col items-center leading-none w-full px-4">

      {/* Date Pill */}
      <div
        className="rounded-full border border-[#3a3a3a] bg-[#111111] flex items-center justify-center"
        style={{
          marginBottom: 'var(--kiosk-date-gap)',
          padding: 'var(--kiosk-date-pad-v) var(--kiosk-date-pad-h)'
        }}
      >
        <span
          className="uppercase font-semibold block whitespace-nowrap"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 'var(--kiosk-date-size)',
            letterSpacing: '0.18em',
            color: '#DEDEDE'
          }}
        >
          {date}
        </span>
      </div>

      {/* Time row — landscape: side-by-side | portrait: stacked */}
      <div
        className="flex justify-center w-full"
        style={{
          flexDirection: isPortrait ? 'column' : 'row',
          alignItems: isPortrait ? 'center' : 'baseline',
          gap: isPortrait ? 'var(--kiosk-meridiem-pad-b)' : 'var(--kiosk-clock-gap)'
        }}
      >
        <span
          className="font-black text-white leading-none"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 'var(--kiosk-clock-size)',
            letterSpacing: '-0.04em',
            lineHeight: 0.88
          }}
        >
          {timePart}
        </span>
        <span
          className="font-black leading-none"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 'var(--kiosk-meridiem-size)',
            color: '#999999',
            letterSpacing: '0.06em',
            /* In landscape, nudge AM/PM down to align with baseline of digits */
            alignSelf: isPortrait ? 'center' : 'flex-end',
            paddingBottom: isPortrait ? 0 : 'var(--kiosk-meridiem-pad-b)'
          }}
        >
          {meridiem}
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   PreRollCountdown
   CSS vars handle vw→vmin switch via @media.
───────────────────────────────────────── */
function PreRollCountdown({ seconds }) {
  return (
    <div className="flex flex-col items-center leading-none">
      <span
        className="uppercase leading-none text-center"
        style={{
          color: '#CCCCCC',
          fontFamily: "'Inter', sans-serif",
          fontSize: 'var(--kiosk-preroll-label)',
          marginBottom: 'var(--kiosk-preroll-gap)',
          fontWeight: 600,
          letterSpacing: '0.28em'
        }}
      >
        WE ARE LIVE IN
      </span>
      <span
        className="font-bold leading-none tabular-nums"
        style={{
          fontFamily: "'Roboto Mono', 'JetBrains Mono', monospace",
          fontSize: 'var(--kiosk-preroll-digit)',
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
          color: '#D4AF37',
          lineHeight: 0.88
        }}
      >
        {seconds}
      </span>
    </div>
  );
}

/* ─────────────────────────────────────────
   ActiveCountdown — the crown jewel
   CSS vars handle vw→vmin switch via @media.
   whiteSpace: nowrap = zero layout shift, ever.
───────────────────────────────────────── */
function ActiveCountdown({ display, paused, fracState }) {
  return (
    <div
      className="flex items-center justify-center leading-none w-full overflow-hidden"
      style={{ padding: '0 2vw' }}
    >
      <span
        key={fracState.key}
        className={`font-bold leading-none tabular-nums${fracState.pulse ? ' timer-pulse' : ''}`}
        style={{
          fontFamily: "'Roboto Mono', 'JetBrains Mono', monospace",
          fontSize: 'var(--kiosk-timer-size)',
          letterSpacing: '0.01em',
          fontVariantNumeric: 'tabular-nums',
          color: fracState.color,
          opacity: paused ? 0.25 : 1,
          transition: 'color 700ms ease-in-out, opacity 700ms ease-in-out',
          lineHeight: 0.88,
          whiteSpace: 'nowrap'
        }}
      >
        {display}
      </span>
    </div>
  );
}

/* ─────────────────────────────────────────
   CompleteState
   CSS vars handle vw→vmin switch via @media.
───────────────────────────────────────── */
function CompleteState() {
  return (
    <div className="flex flex-col items-center justify-center w-full px-8 text-center">
      <div
        style={{
          width: 'var(--kiosk-complete-bar-w)',
          height: 'var(--kiosk-complete-bar-h)',
          backgroundColor: '#dc2626',
          borderRadius: '9999px',
          marginBottom: 'var(--kiosk-complete-gap)'
        }}
      />
      <span
        className="uppercase block text-center"
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 'var(--kiosk-complete-text)',
          fontWeight: 800,
          letterSpacing: '0.18em',
          paddingLeft: '0.18em',
          color: '#dc2626',
          lineHeight: 1.0
        }}
      >
        SESSION<br />COMPLETE
      </span>
      <div
        style={{
          width: 'var(--kiosk-complete-bar-w)',
          height: 'var(--kiosk-complete-bar-h)',
          backgroundColor: '#dc2626',
          borderRadius: '9999px',
          marginTop: 'var(--kiosk-complete-gap)'
        }}
      />
    </div>
  );
}